import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { JobCard } from "@/components/JobCard";
import { JobDetailModal } from "@/components/JobDetailModal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useSmartApply } from "@/hooks/useSmartApply";

interface RecentJob {
  id: string;
  title: string;
  company: string;
  location?: string;
  salary?: string;
  description?: string;
  job_url: string;
  apply_url?: string;
  posted_at?: string;
  source: string;
  apply_type?: string;
  logo?: string;
  contract_type?: string;
  work_type?: string;
  searched_at: string;
  search_query?: string;
  search_location?: string;
}

export function RecentEasyApplyJobs() {
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [filterQuery, setFilterQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [selectedJob, setSelectedJob] = useState<RecentJob | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { addToSmartApply } = useSmartApply();

  useEffect(() => {
    loadRecentJobs();
    loadSavedJobs();
    
    // Listen for search completion to reload recent jobs
    const handleReload = () => {
      loadRecentJobs();
    };
    
    window.addEventListener('reloadRecentJobs', handleReload);
    
    return () => {
      window.removeEventListener('reloadRecentJobs', handleReload);
    };
  }, []);

  const loadRecentJobs = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_searched_jobs')
        .select(`
          searched_at,
          search_query,
          search_location,
          scraped_jobs (*)
        `)
        .eq('user_id', user.id)
        .order('searched_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const jobs = data?.map(r => ({
        ...(r.scraped_jobs as any),
        searched_at: r.searched_at,
        search_query: r.search_query,
        search_location: r.search_location,
      })) || [];

      setRecentJobs(jobs);
    } catch (error) {
      console.error('Error loading recent jobs:', error);
      toast.error('Failed to load recent jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSavedJobs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("saved_jobs")
      .select("job_id")
      .eq("user_id", user.id);

    if (data) {
      setSavedJobIds(new Set(data.map(j => j.job_id)));
    }
  };

  const handleSaveJob = async (job: RecentJob) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Please sign in to save jobs");
      navigate(`/login?redirect=/app/job-search`);
      return;
    }

    const jobId = job.id;
    const isSaved = savedJobIds.has(jobId);

    try {
      if (isSaved) {
        await supabase
          .from("saved_jobs")
          .delete()
          .eq("user_id", user.id)
          .eq("job_id", jobId);
        
        setSavedJobIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
        
        toast.success("Job removed from saved");
      } else {
        const { error } = await supabase.from("saved_jobs").insert({
          user_id: user.id,
          job_id: jobId,
          title: job.title,
          company: job.company,
          location: job.location,
          posted_date: job.posted_at,
          job_type: job.contract_type || 'Full-time',
          remote: job.work_type || 'On-site',
          description: job.description || "",
          url: job.job_url,
          salary: job.salary,
          logo: job.logo
        });

        if (error) throw error;
        
        setSavedJobIds(prev => new Set(prev).add(jobId));
        toast.success("Job saved!");
      }
    } catch (error: any) {
      console.error("Error saving job:", error);
      toast.error("Failed to save job");
    }
  };

  const handleSmartApplyClick = async (job: RecentJob) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Please sign in to use Smart Apply');
      navigate('/login?redirect=/app/job-search');
      return;
    }
    
    await addToSmartApply({
      job_id: job.id,
      title: job.title,
      company: job.company,
      location: job.location,
      description: job.description,
      url: job.apply_url || job.job_url,
      salary: job.salary,
      remote: job.work_type,
      job_type: job.contract_type,
      logo: job.logo,
    });
  };

  const handleJobClick = (job: RecentJob) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedJob(null);
    setIsModalOpen(false);
  };

  const filteredJobs = recentJobs.filter(job => 
    !filterQuery || 
    job.title.toLowerCase().includes(filterQuery.toLowerCase()) ||
    job.company.toLowerCase().includes(filterQuery.toLowerCase()) ||
    job.location?.toLowerCase().includes(filterQuery.toLowerCase())
  );

  const displayedJobs = showAll ? filteredJobs : filteredJobs.slice(0, 10);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3" />
          <div className="h-10 bg-muted rounded" />
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-muted rounded" />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Recent Easy Apply Jobs</h2>
        <span className="text-sm text-muted-foreground">
          Your past search results
        </span>
      </div>

      {recentJobs.length > 0 ? (
        <>
          <Input
            placeholder="Search your recent jobs..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="mb-4"
          />

          <div className="space-y-4">
            {displayedJobs.map(job => (
              <JobCard
                key={job.id}
                job={{
                  id: job.id,
                  title: job.title,
                  company: job.company,
                  location: job.location,
                  salary: job.salary,
                  description: job.description,
                  job_url: job.apply_url || job.job_url,
                  posted_at: job.posted_at,
                  source: (job.source as 'apify_linkedin' | 'jsearch') || 'apify_linkedin',
                  apply_type: (job.apply_type as 'EASY_APPLY' | 'EXTERNAL') || 'EASY_APPLY',
                  saved: savedJobIds.has(job.id),
                  job_type: job.contract_type,
                  remote: job.work_type,
                  logo: job.logo,
                }}
                onSmartApply={() => handleSmartApplyClick(job)}
                onSave={() => handleSaveJob(job)}
                onClick={() => handleJobClick(job)}
              />
            ))}
          </div>

          {filteredJobs.length > 10 && (
            <div className="mt-4 text-center">
              <Button variant="ghost" onClick={() => setShowAll(!showAll)}>
                {showAll ? 'Show Less' : `Show All (${filteredJobs.length} jobs)`}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-lg mb-2">No recent jobs yet.</p>
          <p className="text-sm">Search for jobs above to build your list!</p>
        </div>
      )}

      {selectedJob && (
        <JobDetailModal
          job={{
            id: selectedJob.id,
            title: selectedJob.title,
            company: selectedJob.company,
            location: selectedJob.location,
            salary: selectedJob.salary,
            description: selectedJob.description,
            url: selectedJob.apply_url || selectedJob.job_url,
            posted_at: selectedJob.posted_at,
            source: (selectedJob.source as 'apify_linkedin' | 'jsearch') || 'apify_linkedin',
            apply_type: (selectedJob.apply_type as 'EASY_APPLY' | 'EXTERNAL') || 'EASY_APPLY',
            logo: selectedJob.logo,
          }}
          open={isModalOpen}
          onOpenChange={handleCloseModal}
        />
      )}
    </Card>
  );
}
